import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Attach the JWT (stored inside the logged-in user object) to every request.
client.interceptors.request.use((config) => {
  try {
    const stored = JSON.parse(localStorage.getItem("ibizz_user") || "null");
    if (stored?.token) config.headers.Authorization = `Bearer ${stored.token}`;
  } catch (e) { /* ignore */ }
  return config;
});

export const calculateTax = async (payload) => {
  const { data } = await client.post("/tax/calculate", payload);
  return data;
};

export const saveComputation = async (payload) => {
  const { data } = await client.post("/computations", payload);
  return data;
};

export const updateComputation = async (id, payload) => {
  const { data } = await client.put(`/computations/${id}`, payload);
  return data;
};

export const listComputations = async (companyId) => {
  const { data } = await client.get("/computations", {
    params: companyId ? { companyId } : {},
  });
  return data;
};

export const deleteComputation = async (id) => {
  const { data } = await client.delete(`/computations/${id}`);
  return data;
};

export const submitComputation = async (id, email, password) => {
  const { data } = await client.post(`/computations/${id}/submit`, { email, password });
  return data;
};

export const unlockComputation = async (id, email, password) => {
  const { data } = await client.post(`/computations/${id}/unlock`, { email, password });
  return data;
};

export const getComputation = async (id) => {
  const { data } = await client.get(`/computations/${id}`);
  return data;
};

export const getDataEntry = async (companyId) => {
  const { data } = await client.get(`/dataentry/${companyId}`);
  return data;
};

export const saveDataEntry = async (companyId, formData) => {
  const { data } = await client.put(`/dataentry/${companyId}`, { data: formData });
  return data;
};

export const authLogin = async (email, password) => {
  const { data } = await client.post("/auth/login", { email, password });
  return data;
};

export const authRegister = async (email, password, name) => {
  const { data } = await client.post("/auth/register", { email, password, name });
  return data;
};

export const authForgot = async (email) => {
  const { data } = await client.post("/auth/forgot", { email });
  return data;
};

// ---- Admin user management ---- //
export const listUsers = async () => {
  const { data } = await client.get("/users");
  return data;
};

export const createUser = async (payload) => {
  const { data } = await client.post("/users", payload);
  return data;
};

export const deleteUser = async (id) => {
  const { data } = await client.delete(`/users/${id}`);
  return data;
};

export const resetUserPassword = async (id, password) => {
  const { data } = await client.put(`/users/${id}/password`, { password });
  return data;
};

export const updateUserName = async (id, name) => {
  const { data } = await client.patch(`/users/${id}`, { name });
  return data;
};

export const updateUserRole = async (id, role) => {
  const { data } = await client.patch(`/users/${id}`, { role });
  return data;
};

// ---- Per-user companies (client list) ---- //
export const getCompanies = async () => {
  const { data } = await client.get("/companies");
  return data;
};

export const getCompany = async (id) => {
  const list = await getCompanies();
  return (list || []).find((c) => c.id === id) || null;
};

export const upsertCompany = async (payload) => {
  const { data } = await client.post("/companies", payload);
  return data;
};

export const updateCompanyCp204 = async (id, patch) => {
  const { data } = await client.patch(`/companies/${id}/cp204`, patch);
  return data;
};

export const downloadCp204Template = async () => {
  const res = await client.get("/cp204/template", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "cp204_compliance_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const importCp204 = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await client.post("/cp204/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

// ---- CP204 compliance entries (standalone, independent of companies) ---- //
export const getCp204Entries = async () => {
  const { data } = await client.get("/cp204/entries");
  return data;
};

export const createCp204Entry = async (payload) => {
  const { data } = await client.post("/cp204/entries", payload);
  return data;
};

export const updateCp204Entry = async (id, patch) => {
  const { data } = await client.patch(`/cp204/entries/${id}`, patch);
  return data;
};

export const deleteCp204Entry = async (id) => {
  const { data } = await client.delete(`/cp204/entries/${id}`);
  return data;
};

export const exportCp204 = async (rows) => {
  const res = await client.post("/cp204/export", { rows }, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "cp204_compliance.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteCompany = async (id, email, password) => {
  const { data } = await client.delete(`/companies/${id}`, { data: { email, password } });
  return data;
};

// ---- Access tokens (credit-based) ---- //
export const getMe = async () => {
  const { data } = await client.get("/me");
  return data;
};

export const listTokens = async () => {
  const { data } = await client.get("/tokens");
  return data;
};

export const createToken = async (credits, note) => {
  const { data } = await client.post("/tokens", { credits: Number(credits), note: note || "" });
  return data;
};

export const revokeToken = async (id) => {
  const { data } = await client.delete(`/tokens/${id}`);
  return data;
};

export const redeemToken = async (code) => {
  const { data } = await client.post("/tokens/redeem", { code });
  return data;
};

export const grantCredits = async (email, credits) => {
  const { data } = await client.post("/credits/grant", { email, credits: Number(credits) });
  return data;
};

// ---- Capital allowance Excel template ---- //
export const downloadCapitalAllowanceTemplate = async () => {
  const res = await client.get("/capital-allowance/template", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = "capital_allowance_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const importCapitalAllowance = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await client.post("/capital-allowance/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const exportCapitalAllowance = async (assets, companyName) => {
  const res = await client.post(
    "/capital-allowance/export",
    { assets, companyName },
    { responseType: "blob" }
  );
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  const safe = (companyName || "capital_allowance").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") || "capital_allowance";
  a.download = `${safe}_capital_allowance.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const reviewTaxComputation = async (payload) => {
  const { data } = await client.post("/tax/review", payload, { timeout: 120000 });
  return data;
};

export const importComprehensiveIncome = async (fileObj) => {
  const fd = new FormData();
  fd.append("file", fileObj);
  const { data } = await client.post("/tax/import-ci", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};
